import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from './auth.service';
import { DEFAULT_PRIORITIZED_ACTIVITIES } from '../components/onboarding/inductionModules';

export interface DegreeEntry {
  level: string; // 'preparatoria' | 'tecnico' | 'licenciatura' | 'especialidad' | 'maestria' | 'doctorado' | 'posdoctorado'
  institution: string;
  title: string;
  status: string; // 'completado' | 'en_curso' | 'trunco'
}

export interface SocialMediaHandles {
  facebook: string;
  instagram: string;
  tiktok: string;
  x: string;
  youtube: string;
  linkedin: string;
}

export interface OnboardingPhase1Data {
  userType: 'SS' | 'VG';
  email: string;
  password?: string;
  names: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  lastnames?: string;
  birthDate: string;
  phone: string;
  hasWhatsapp: boolean;
  whatsappNumber: string;
  hasSecondaryContact: boolean;
  secondaryPhone: string;
  // Solo Estudiante (SS):
  curp: string;
  studentId: string;
  academicLevel: string;
  university: string;
  carrera: string;
  periodType: string;
  periodNumber?: number | string;
  semester: string;
  serviceStartDate?: string;
  serviceEndDate?: string;
  // Solo Voluntario General (VG):
  occupation: string;
  degrees: DegreeEntry[];
  // Redes sociales (Opcional - ambos roles)
  socialMedia: SocialMediaHandles;
}

export interface ModuleReflection {
  liked: string;
  important: string;
  improve: string;
}

export interface OnboardingPhase2Data {
  socialsFollowed: boolean;
  completedModules: number[];
  reflections: {
    'modulo-1': ModuleReflection;
    'modulo-2': ModuleReflection;
    'modulo-3': ModuleReflection;
    'modulo-4': ModuleReflection;
    'modulo-5': ModuleReflection;
    'modulo-6': ModuleReflection;
    [key: string]: any;
  };
}

export interface OnboardingPhase3Data {
  bioPersonal: string;
  intereses: string;
  talentos: string;
  aportesAbaq: string;
  animalExperience: 'actual' | 'pasada' | 'sin_experiencia' | '';
  descExperiencia: string;
  motivacionAbaq: string;
  rankedActivities: string[];
}

export interface OnboardingPhase4Data {
  cloudUrls: {
    ine: string;
    cv: string;
    comprobante: string;
  };
  consents: {
    reglamento: boolean;
    privacidad: boolean;
    etica: boolean;
    incognito: boolean;
    consentimiento: boolean;
  };
}

export interface OnboardingState {
  phase1: OnboardingPhase1Data;
  phase2: OnboardingPhase2Data;
  phase3: OnboardingPhase3Data;
  phase4: OnboardingPhase4Data;
  completedPhases: number[];
  expediente: {
    folio: string;
    accountStatus: 'pending' | 'active' | 'rejected';
    playCompletionSequence: boolean;
    fechaInicioFormularioExpediente?: string;
    fechaFinFormularioExpediente?: string;
    fechaEmisionExpediente?: string;
    fechaAceptacionExpediente?: string;
    fechaExpedicionExpediente?: string;
    tiempoLlenadoMinutosExpediente?: number;
  };
}

const STORAGE_KEY = 'abaq_angular_onboarding_v6';

function buildDefaultFolio(userType: 'SS' | 'VG', suffix = '01'): string {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${userType}-${yy}${mm}${dd}-${suffix}`;
}

function createInitialState(): OnboardingState {
  return {
    phase1: {
      userType: 'SS',
      email: '',
      password: '',
      names: '',
      apellidoPaterno: '',
      apellidoMaterno: '',
      birthDate: '',
      phone: '',
      hasWhatsapp: true,
      whatsappNumber: '',
      hasSecondaryContact: false,
      secondaryPhone: '',
      curp: '',
      studentId: '',
      academicLevel: '',
      university: '',
      carrera: '',
      periodType: 'semestre',
      periodNumber: 1,
      semester: '1er Semestre',
      occupation: '',
      degrees: [
        {
          level: 'licenciatura',
          institution: '',
          title: '',
          status: 'completado',
        },
      ],
      socialMedia: {
        facebook: '',
        instagram: '',
        tiktok: '',
        x: '',
        youtube: '',
        linkedin: '',
      },
    },
    phase2: {
      socialsFollowed: false,
      completedModules: [],
      reflections: {
        'modulo-1': { liked: '', important: '', improve: '' },
        'modulo-2': { liked: '', important: '', improve: '' },
        'modulo-3': { liked: '', important: '', improve: '' },
        'modulo-4': { liked: '', important: '', improve: '' },
        'modulo-5': { liked: '', important: '', improve: '' },
        'modulo-6': { liked: '', important: '', improve: '' },
      },
    },
    phase3: {
      bioPersonal: '',
      intereses: '',
      talentos: '',
      aportesAbaq: '',
      animalExperience: '',
      descExperiencia: '',
      motivacionAbaq: '',
      rankedActivities: [...DEFAULT_PRIORITIZED_ACTIVITIES],
    },
    phase4: {
      cloudUrls: {
        ine: '',
        cv: '',
        comprobante: '',
      },
      consents: {
        reglamento: false,
        privacidad: false,
        etica: false,
        incognito: false,
        consentimiento: false,
      },
    },
    completedPhases: [],
    expediente: {
      folio: buildDefaultFolio('SS'),
      accountStatus: 'pending',
      playCompletionSequence: true,
      fechaInicioFormularioExpediente: new Date().toISOString(),
      fechaFinFormularioExpediente: '',
      fechaEmisionExpediente: '',
      fechaAceptacionExpediente: '',
      fechaExpedicionExpediente: '',
      tiempoLlenadoMinutosExpediente: 0,
    },
  };
}

@Injectable({
  providedIn: 'root',
})
export class OnboardingStateService {
  private authService = inject(AuthService);
  private stateSubject = new BehaviorSubject<OnboardingState>(this.loadFromStorage());
  state$ = this.stateSubject.asObservable();

  get state(): OnboardingState {
    return this.stateSubject.value;
  }

  private loadFromStorage(): OnboardingState {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return createInitialState();
      const parsed = JSON.parse(raw);
      const base = createInitialState();
      return {
        ...base,
        ...parsed,
        phase1: {
          ...base.phase1,
          ...(parsed.phase1 || {}),
          socialMedia: {
            ...base.phase1.socialMedia,
            ...(parsed.phase1?.socialMedia || {}),
          },
        },
        phase2: {
          ...base.phase2,
          ...(parsed.phase2 || {}),
          reflections: { ...base.phase2.reflections, ...(parsed.phase2?.reflections || {}) },
        },
        phase3: {
          ...base.phase3,
          ...(parsed.phase3 || {}),
          rankedActivities:
            Array.isArray(parsed.phase3?.rankedActivities) &&
            parsed.phase3.rankedActivities.length === DEFAULT_PRIORITIZED_ACTIVITIES.length
              ? parsed.phase3.rankedActivities
              : [...DEFAULT_PRIORITIZED_ACTIVITIES],
        },
        phase4: {
          ...base.phase4,
          ...(parsed.phase4 || {}),
          cloudUrls: { ...base.phase4.cloudUrls, ...(parsed.phase4?.cloudUrls || {}) },
          consents: { ...base.phase4.consents, ...(parsed.phase4?.consents || {}) },
        },
        expediente: {
          ...base.expediente,
          ...(parsed.expediente || {}),
          fechaInicioFormularioExpediente:
            parsed.expediente?.fechaInicioFormularioExpediente ||
            base.expediente.fechaInicioFormularioExpediente,
        },
      };
    } catch {
      return createInitialState();
    }
  }

  private saveToStorage(next: OnboardingState): void {
    this.stateSubject.next(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore storage quota errors
    }
  }

  resetForNewRegistration(userType: 'SS' | 'VG', email: string, password: string): void {
    const fresh = createInitialState();
    fresh.phase1.userType = userType;
    fresh.phase1.email = email;
    fresh.phase1.password = password;
    fresh.expediente.folio = buildDefaultFolio(userType);
    fresh.expediente.fechaInicioFormularioExpediente = new Date().toISOString();
    this.saveToStorage(fresh);
  }

  clearState(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    this.stateSubject.next(createInitialState());
  }

  updatePhase1(patch: Partial<OnboardingPhase1Data>): void {
    const current = this.state;
    const nextUserType = patch.userType || current.phase1.userType;
    const next: OnboardingState = {
      ...current,
      phase1: { ...current.phase1, ...patch },
      expediente: {
        ...current.expediente,
        folio: buildDefaultFolio(nextUserType),
      },
    };
    this.saveToStorage(next);
  }

  updatePhase2(patch: Partial<OnboardingPhase2Data>): void {
    const current = this.state;
    this.saveToStorage({
      ...current,
      phase2: { ...current.phase2, ...patch },
    });
  }

  updatePhase3(patch: Partial<OnboardingPhase3Data>): void {
    const current = this.state;
    this.saveToStorage({
      ...current,
      phase3: { ...current.phase3, ...patch },
    });
  }

  updatePhase4(patch: Partial<OnboardingPhase4Data>): void {
    const current = this.state;
    this.saveToStorage({
      ...current,
      phase4: {
        ...current.phase4,
        ...patch,
        cloudUrls: { ...current.phase4.cloudUrls, ...(patch.cloudUrls || {}) },
        consents: { ...current.phase4.consents, ...(patch.consents || {}) },
      },
    });
  }

  markPhaseCompleted(phase: number): void {
    const current = this.state;
    const set = new Set(current.completedPhases || []);
    set.add(phase);
    this.saveToStorage({
      ...current,
      completedPhases: Array.from(set),
    });
  }

  setExpedienteState(patch: Partial<OnboardingState['expediente']>): void {
    const current = this.state;
    this.saveToStorage({
      ...current,
      expediente: { ...current.expediente, ...patch },
    });
  }

  /**
   * Conecta las 4 fases al backend real al finalizar la Fase 4:
   * - Si es SS -> POST /auth/signup-student (Student en MongoDB)
   * - Si es VG -> POST /auth/signup-volunteer (Volunteer en MongoDB)
   */
  async submitDossierToBackend(): Promise<{
    ok: boolean;
    error?: string;
    folio?: string;
  }> {
    const { phase1, phase2, phase3, phase4 } = this.state;
    const fullName = `${phase1.names || ''} ${phase1.apellidoPaterno || ''} ${phase1.apellidoMaterno || ''}`.trim() || phase1.names || 'Usuario ABAQ';
    const cleanPhone = (phase1.phone || '').replace(/\D/g, '');
    const email = (phase1.email || '').trim().toLowerCase();
    const password = phase1.password || '';

    if (!email) {
      return {
        ok: false,
        error: 'Falta el correo electrónico en la Fase 1.',
      };
    }

    if (!password || password.length < 8) {
      return {
        ok: false,
        error: 'La contraseña de tu cuenta debe tener al menos 8 caracteres (revisa la Fase 1).',
      };
    }

    try {
      const now = new Date();
      const fechaFin = now.toISOString();
      const fechaEmision = fechaFin;
      const fechaInicio = this.state.expediente.fechaInicioFormularioExpediente || fechaFin;
      const diffMs = Math.max(0, new Date(fechaFin).getTime() - new Date(fechaInicio).getTime());
      const tiempoLlenadoMinutos = Number((diffMs / 60000).toFixed(1));

      if (phase1.userType === 'SS') {
        const res = await this.authService.signUpWithStudent(email, password, {
          folio: this.state.expediente.folio,
          names: phase1.names,
          apellidoPaterno: phase1.apellidoPaterno,
          apellidoMaterno: phase1.apellidoMaterno,
          nombreCompleto: fullName,
          matricula: (phase1.studentId || '2024-UAQ-001').trim(),
          curp: (phase1.curp || '').trim().toUpperCase(),
          correoInstitucional: email,
          correoPersonal: email,
          escuela: (phase1.university || 'Universidad Autónoma de Querétaro (UAQ)').trim(),
          carrera: (phase1.carrera || 'Licenciatura').trim(),
          academicLevel: phase1.academicLevel || 'licenciatura',
          periodType: phase1.periodType || 'semestre',
          periodNumber: phase1.periodNumber || 1,
          semestre: phase1.semester || '1er Semestre',
          semester: phase1.semester || '1er Semestre',
          telefono: cleanPhone || '4425128840',
          hasWhatsapp: phase1.hasWhatsapp,
          whatsappNumber: phase1.hasWhatsapp ? cleanPhone : (phase1.whatsappNumber || '').replace(/\D/g, ''),
          hasSecondaryContact: phase1.hasSecondaryContact,
          secondaryPhone: phase1.hasSecondaryContact ? (phase1.secondaryPhone || '').replace(/\D/g, '') : '',
          fechaNacimiento: phase1.birthDate || '2001-01-01',
          fechaArranque: phase1.serviceStartDate || new Date(),
          fechaConclusion: phase1.serviceEndDate || undefined,
          serviceStartDate: phase1.serviceStartDate || '',
          serviceEndDate: phase1.serviceEndDate || '',
          facebookUsuario: (phase1.socialMedia?.facebook || '').trim(),
          instagramUsuario: (phase1.socialMedia?.instagram || '').trim(),
          tiktokUsuario: (phase1.socialMedia?.tiktok || '').trim(),
          xUsuario: (phase1.socialMedia?.x || '').trim(),
          youtubeUsuario: (phase1.socialMedia?.youtube || '').trim(),
          linkedinUsuario: (phase1.socialMedia?.linkedin || '').trim(),
          bioPersonal: phase3.bioPersonal || '',
          intereses: phase3.intereses || '',
          talentos: phase3.talentos || '',
          aportesAbaq: phase3.aportesAbaq || '',
          animalExperience: phase3.animalExperience || '',
          descExperiencia: phase3.descExperiencia || '',
          motivacionAbaq: phase3.motivacionAbaq || '',
          rankedActivities: phase3.rankedActivities || [],
          cloudUrls: phase4.cloudUrls,
          consents: phase4.consents,
          fechaInicioFormularioExpediente: fechaInicio,
          fechaFinFormularioExpediente: fechaFin,
          fechaEmisionExpediente: fechaEmision,
          tiempoLlenadoMinutosExpediente: tiempoLlenadoMinutos,
          onboardingData: {
            reflections: phase2.reflections,
            socialsFollowed: phase2.socialsFollowed,
            bioPersonal: phase3.bioPersonal,
            intereses: phase3.intereses,
            talentos: phase3.talentos,
            aportesAbaq: phase3.aportesAbaq,
            animalExperience: phase3.animalExperience,
            descExperiencia: phase3.descExperiencia,
            motivacionAbaq: phase3.motivacionAbaq,
            rankedActivities: phase3.rankedActivities,
            cloudUrls: phase4.cloudUrls,
            consents: phase4.consents,
            fechaInicioFormularioExpediente: fechaInicio,
            fechaFinFormularioExpediente: fechaFin,
            fechaEmisionExpediente: fechaEmision,
            tiempoLlenadoMinutosExpediente: tiempoLlenadoMinutos,
          },
        } as any);

        const returnedDoc = (res as any)?.data?.document;
        const docId = returnedDoc?._id || '';
        const suffix = docId ? String(docId).slice(-2).toUpperCase() : '01';
        const folio = returnedDoc?.folio || buildDefaultFolio('SS', suffix);
        this.setExpedienteState({
          folio,
          accountStatus: 'pending',
          playCompletionSequence: true,
          fechaInicioFormularioExpediente: fechaInicio,
          fechaFinFormularioExpediente: fechaFin,
          fechaEmisionExpediente: fechaEmision,
          tiempoLlenadoMinutosExpediente: tiempoLlenadoMinutos,
        });
        sessionStorage.setItem('abaq_play_completion_anim', 'true');
        return { ok: true, folio };
      } else {
        const res = await this.authService.signUpWithVolunteer(email, password, {
          folio: this.state.expediente.folio,
          volunteerType: 'GENERAL',
          names: phase1.names,
          apellidoPaterno: phase1.apellidoPaterno,
          apellidoMaterno: phase1.apellidoMaterno,
          nombreCompleto: fullName,
          curp: (phase1.curp || '').trim().toUpperCase(),
          correoPersonal: email,
          telefono: cleanPhone || '4425128840',
          hasWhatsapp: phase1.hasWhatsapp,
          whatsappNumber: phase1.hasWhatsapp ? cleanPhone : (phase1.whatsappNumber || '').replace(/\D/g, ''),
          hasSecondaryContact: phase1.hasSecondaryContact,
          secondaryPhone: phase1.hasSecondaryContact ? (phase1.secondaryPhone || '').replace(/\D/g, '') : '',
          fechaNacimiento: phase1.birthDate || '2000-01-01',
          occupation: phase1.occupation,
          degrees: phase1.degrees,
          facebookUsuario: (phase1.socialMedia?.facebook || '').trim(),
          instagramUsuario: (phase1.socialMedia?.instagram || '').trim(),
          tiktokUsuario: (phase1.socialMedia?.tiktok || '').trim(),
          xUsuario: (phase1.socialMedia?.x || '').trim(),
          youtubeUsuario: (phase1.socialMedia?.youtube || '').trim(),
          linkedinUsuario: (phase1.socialMedia?.linkedin || '').trim(),
          bioPersonal: phase3.bioPersonal || '',
          intereses: phase3.intereses || '',
          talentos: phase3.talentos || '',
          aportesAbaq: phase3.aportesAbaq || '',
          animalExperience: phase3.animalExperience || '',
          descExperiencia: phase3.descExperiencia || '',
          motivacionAbaq: phase3.motivacionAbaq || '',
          rankedActivities: phase3.rankedActivities || [],
          cloudUrls: phase4.cloudUrls,
          consents: phase4.consents,
          fechaInicioFormularioExpediente: fechaInicio,
          fechaFinFormularioExpediente: fechaFin,
          fechaEmisionExpediente: fechaEmision,
          tiempoLlenadoMinutosExpediente: tiempoLlenadoMinutos,
          onboardingData: {
            reflections: phase2.reflections,
            socialsFollowed: phase2.socialsFollowed,
            bioPersonal: phase3.bioPersonal,
            intereses: phase3.intereses,
            talentos: phase3.talentos,
            aportesAbaq: phase3.aportesAbaq,
            animalExperience: phase3.animalExperience,
            descExperiencia: phase3.descExperiencia,
            motivacionAbaq: phase3.motivacionAbaq,
            rankedActivities: phase3.rankedActivities,
            cloudUrls: phase4.cloudUrls,
            consents: phase4.consents,
            fechaInicioFormularioExpediente: fechaInicio,
            fechaFinFormularioExpediente: fechaFin,
            fechaEmisionExpediente: fechaEmision,
            tiempoLlenadoMinutosExpediente: tiempoLlenadoMinutos,
          },
        } as any);

        const returnedDoc = (res as any)?.data?.document;
        const docId = returnedDoc?._id || '';
        const suffix = docId ? String(docId).slice(-2).toUpperCase() : '01';
        const folio = returnedDoc?.folio || buildDefaultFolio('VG', suffix);
        this.setExpedienteState({
          folio,
          accountStatus: 'pending',
          playCompletionSequence: true,
          fechaInicioFormularioExpediente: fechaInicio,
          fechaFinFormularioExpediente: fechaFin,
          fechaEmisionExpediente: fechaEmision,
          tiempoLlenadoMinutosExpediente: tiempoLlenadoMinutos,
        });
        sessionStorage.setItem('abaq_play_completion_anim', 'true');
        return { ok: true, folio };
      }
    } catch (err: any) {
      const rawMsg = err?.error?.message || err?.message || '';
      const friendlyMsg =
        rawMsg === 'Email already registered'
          ? `El correo "${email}" ya está registrado en ABAQ. Usa otro correo en la Fase 1 o inicia sesión.`
          : rawMsg || 'No se pudo crear el usuario en la base de datos. Revisa tus datos e intenta nuevamente.';
      return {
        ok: false,
        error: friendlyMsg,
      };
    }
  }
}
